import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plug,
  Lock,
  Plus,
  RefreshCw,
  Settings2,
  FlaskConical,
  Unplug,
  ShieldCheck,
  Eye,
  Mail,
  Send,
  Activity,
  Webhook,
  Gauge,
  KeyRound,
  ArrowRight,
  BookOpen,
  Check,
  ExternalLink,
  Loader2,
  Search,
  X,
  LayoutGrid,
  Share2,
  AlertTriangle,
  FolderGit2,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useWebhooks } from "@/store/webhookStore";
import { APPS } from "@/data/apps";
import {
  INT_CATEGORIES,
  INT_STATUS_LABEL,
  INT_STATUS_TONE,
  INT_HEALTH_LABEL,
  REQUEST_REASONS,
} from "@/data/integrationsHub";
import { AppHeader, Badge } from "@/components/ui/primitives";
import { Modal } from "@/components/ui/Modal";
import { SampleBadge } from "@/components/ui/SampleBadge";
import { isVisible } from "@/lib/entitlements";
import { timeAgo } from "@/lib/utils";
import { Topology } from "./integration/Topology";
import { ConnectAppModal } from "./integration/ConnectAppModal";
import { WebhooksPanel } from "./integration/WebhooksPanel";
import { IntegrationGuideModal } from "./integration/IntegrationGuide";
import type { HiveIntegration } from "@/types";

const accent = APPS.integrations.accent;

const HEALTH_COLOR: Record<HiveIntegration["health"], string> = {
  healthy: "#22c55e",
  delayed: "#f59e0b",
  "rate-limited": "#f59e0b",
  "reauth-required": "#f43f5e",
  "permission-missing": "#f43f5e",
  "webhook-failed": "#f43f5e",
  "admin-locked": "#a855f7",
};

function HealthDot({ health }: { health: HiveIntegration["health"] }) {
  const color = HEALTH_COLOR[health];
  const pulse = health !== "healthy" && health !== "admin-locked";
  return (
    <span className="relative flex h-2 w-2">
      {pulse && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: color }} />}
      <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: color }} />
    </span>
  );
}

function IntegrationCard({ it, index }: { it: HiveIntegration; index: number }) {
  const selectIntegration = useHive((s) => s.selectIntegration);
  const selectedId = useHive((s) => s.selectedIntegrationId);
  const setConnectCatalog = useHive((s) => s.setConnectCatalog);
  const reauth = useHive((s) => s.reauthIntegration);
  const selected = selectedId === it.id;

  const primary = () => {
    if (it.admin) return null;
    if (it.status === "not-connected" || it.status === "pending")
      return { label: "Connect", icon: Plus, run: () => setConnectCatalog(true, it.id), solid: true };
    if (it.status === "needs-reauth")
      return {
        label: "Reauthorize",
        icon: RefreshCw,
        // With a stored credential re-verify it live; otherwise ask for a new one.
        run: () => (it.hasCredentials ? reauth(it.id) : setConnectCatalog(true, it.id)),
        solid: true,
      };
    return { label: "Configure", icon: Settings2, run: () => selectIntegration(it.id), solid: false };
  };
  const p = primary();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      whileHover={{ y: -2 }}
      onClick={() => selectIntegration(it.id)}
      className="flex cursor-pointer flex-col rounded-2xl border bg-surface/50 p-3.5 transition"
      style={{ borderColor: selected ? accent : "hsl(var(--border))", boxShadow: selected ? `0 10px 28px -16px ${accent}aa` : undefined }}
    >
      <div className="flex items-start gap-2.5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-sm font-bold" style={{ background: `${it.color}22`, color: it.color }}>
          {it.glyph}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-app">{it.name}</span>
            {it.isSample && <SampleBadge />}
            {it.admin && <Lock size={11} className="text-violet-500" />}
          </div>
          <div className="text-[11px] text-app-2">{it.category}</div>
        </div>
        <HealthDot health={it.health} />
      </div>

      <p className="mt-2 line-clamp-2 min-h-[2rem] text-[11px] leading-relaxed text-app-2">{it.description}</p>

      <div className="mt-2 flex items-center gap-1.5">
        <Badge tone={INT_STATUS_TONE[it.status]}>{INT_STATUS_LABEL[it.status]}</Badge>
        <span className="rounded-md bg-surface-2/70 px-1.5 py-0.5 text-[10px] capitalize text-app-2">{it.scope}</span>
      </div>

      <div className="mt-2.5 flex items-center justify-between border-t border-app pt-2.5">
        <span className="text-[10px] text-app-2">
          {it.status === "not-connected" ? INT_HEALTH_LABEL[it.health] : `sync ${it.lastSync}`}
        </span>
        {it.admin ? (
          <span className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium" style={{ background: "#a855f71f", color: "#a855f7" }}>
            <Lock size={10} /> Admin Managed
          </span>
        ) : (
          p && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                p.run();
              }}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold transition hover:brightness-105"
              style={p.solid ? { background: accent, color: "#000" } : { border: "1px solid hsl(var(--border))", color: "var(--text-1)" }}
            >
              <p.icon size={10} /> {p.label}
            </button>
          )
        )}
      </div>
    </motion.div>
  );
}

function PermRow({ label, on }: { label: string; on: boolean }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-app-1">{label}</span>
      <span className={`flex items-center gap-1 ${on ? "text-emerald-500" : "text-app-2"}`}>
        {on ? <Check size={13} /> : <X size={13} />}
        {on ? "Yes" : "No"}
      </span>
    </div>
  );
}

function IntegrationDetail({
  it,
  onRequest,
  onManageWebhooks,
}: {
  it: HiveIntegration;
  onRequest: () => void;
  onManageWebhooks: (integrationId: string) => void;
}) {
  const setConnectCatalog = useHive((s) => s.setConnectCatalog);
  const disconnect = useHive((s) => s.disconnectIntegration);
  const reauth = useHive((s) => s.reauthIntegration);
  const testIntegration = useHive((s) => s.testIntegration);
  const pushToast = useHive((s) => s.pushToast);
  const workspaces = useHive((s) => s.codeWorkspaces);
  const openApp = useHive((s) => s.openApp);
  const aresVisible = useHive((s) => isVisible(s.entitlements, "ares"));
  const allHooks = useWebhooks((s) => s.webhooks);
  const linkedHooks = useMemo(() => allHooks.filter((w) => w.integrationId === it.id), [allHooks, it.id]);
  const linkedWs = workspaces.filter((w) => it.linkedWorkspaces.includes(w.id));
  const showError = Boolean(it.lastError) && !it.isSample;

  return (
    <motion.div key={it.id} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} className="flex h-full flex-col">
      <div className="border-b border-app p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl text-base font-bold" style={{ background: `${it.color}22`, color: it.color }}>
            {it.glyph}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-base font-semibold text-app">{it.name}</span>
              {it.isSample && <SampleBadge />}
              {it.admin && <Lock size={13} className="text-violet-500" />}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-app-2">
              {it.category} · {it.scope} scope
              {it.docsUrl && (
                <a
                  href={it.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={`${it.name} API documentation`}
                  className="text-app-2 transition hover:text-app"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
          <Badge tone={INT_STATUS_TONE[it.status]}>{INT_STATUS_LABEL[it.status]}</Badge>
        </div>

        {/* sticky error — the provider's actual answer plus the way out */}
        {showError && it.lastError && (
          <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-2.5">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-500">
              <AlertTriangle size={12} /> {INT_HEALTH_LABEL[it.health]}
              <span className="ml-auto font-normal text-app-2">{timeAgo(it.lastError.at)} ago</span>
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-app-1">{it.lastError.message}</p>
            {!it.admin && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button
                  onClick={() => setConnectCatalog(true, it.id)}
                  className="flex items-center gap-1 rounded-md bg-rose-500 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-105"
                >
                  <KeyRound size={11} /> Update credential
                </button>
                <button
                  onClick={() => void testIntegration(it.id)}
                  className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
                >
                  <FlaskConical size={11} /> Run test
                </button>
                {it.docsUrl && (
                  <a
                    href={it.docsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
                  >
                    <BookOpen size={11} /> Provider docs
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* actions */}
        {it.admin ? (
          <div className="mt-3 rounded-lg border border-violet-500/30 bg-violet-500/10 p-2.5">
            <div className="flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-300">
              <Lock size={12} /> This integration is managed by your organization administrator.
            </div>
            <div className="mt-2 flex gap-1.5">
              <button onClick={onRequest} disabled={it.requested} className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-105 disabled:opacity-60" style={{ background: "#a855f7" }}>
                <ShieldCheck size={11} /> {it.requested ? "Request pending" : "Request Access"}
              </button>
              <button onClick={() => pushToast({ title: "Admin notified", body: `Message sent about ${it.name}`, tone: "info" })} className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
                <Mail size={11} /> Contact admin
              </button>
              <button className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
                <Eye size={11} /> View scopes
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {it.status === "not-connected" || it.status === "pending" ? (
              <button onClick={() => setConnectCatalog(true, it.id)} className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105" style={{ background: accent }}>
                <Plus size={11} /> Connect
              </button>
            ) : (
              <>
                <button
                  onClick={() => (it.hasCredentials ? reauth(it.id) : setConnectCatalog(true, it.id))}
                  className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
                  style={{ background: accent }}
                >
                  <RefreshCw size={11} /> Reauthorize
                </button>
                <button onClick={() => void testIntegration(it.id)} className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
                  <FlaskConical size={11} /> Test
                </button>
                <button onClick={() => setConnectCatalog(true, it.id)} className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
                  <Settings2 size={11} /> Configure
                </button>
                <button onClick={() => disconnect(it.id)} className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-rose-500 transition hover:border-rose-500/40">
                  <Unplug size={11} /> Disconnect
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto no-scrollbar p-4">
        {/* health + auth + rate limit */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-app bg-surface-2/30 p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-app-2"><Activity size={11} /> Health</div>
            <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-app"><HealthDot health={it.health} /> {INT_HEALTH_LABEL[it.health]}</div>
          </div>
          <div className="rounded-lg border border-app bg-surface-2/30 p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-app-2"><KeyRound size={11} /> Auth</div>
            <div className="mt-1 text-sm font-medium text-app">{it.status === "connected" || it.admin ? "Authenticated" : it.status === "needs-reauth" ? "Token expired" : "Not authenticated"}</div>
          </div>
        </div>

        {/* rate limit */}
        <div>
          <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-app-2">
            <span className="flex items-center gap-1"><Gauge size={11} /> Rate limit</span>
            <span className="tabular-nums">{Math.round(it.rateLimit * 100)}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
            <motion.div className="h-full rounded-full" style={{ background: it.rateLimit > 0.85 ? "#f43f5e" : it.rateLimit > 0.6 ? "#f59e0b" : "#22c55e" }} initial={{ width: 0 }} animate={{ width: `${it.rateLimit * 100}%` }} transition={{ duration: 0.8 }} />
          </div>
        </div>

        {/* permissions / security surface */}
        <div className="rounded-xl border border-app bg-surface-2/30 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
            <ShieldCheck size={12} /> Permissions & security
          </div>
          <div className="space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-app-2">Hive can read</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {it.permissions.read.map((r) => <span key={r} className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-app-1">{r}</span>)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-app-2">Hive can write</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {it.permissions.write.map((r) => <span key={r} className="rounded px-1.5 py-0.5 text-[10px]" style={{ background: `${accent}1a`, color: accent }}>{r}</span>)}
              </div>
            </div>
            <div className="space-y-1.5 border-t border-app pt-2">
              {aresVisible && <PermRow label="ARES can use this integration" on={it.permissions.aresCanUse} />}
              <PermRow label="Auto-resolve can post here" on={it.permissions.autoResolveCanPost} />
              <PermRow label="Admin approval required" on={it.permissions.adminApproval} />
              <PermRow label="Audit logging" on={it.permissions.audit} />
            </div>
          </div>
        </div>

        {/* linked workspaces (cross-widget) */}
        <div>
          <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-app-2">
            <span>Linked workspaces</span>
            <button onClick={() => openApp("workspaces")} className="flex items-center gap-1 text-app-2 hover:text-app"><FolderGit2 size={11} /> <ArrowRight size={11} /></button>
          </div>
          {linkedWs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-2.5 text-center text-[11px] text-app-2">Not linked to any workspace</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {linkedWs.map((w) => (
                <button key={w.id} onClick={() => { useHive.getState().selectWorkspace(w.id); openApp("workspaces"); }} className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2/40 px-2 py-1 text-[11px] text-app-1 transition hover:border-app-strong">
                  <span className="h-2.5 w-2.5 rounded" style={{ background: w.accent }} /> {w.repo}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* webhooks wired to this integration */}
        <div>
          <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-app-2">
            <span className="flex items-center gap-1.5"><Webhook size={12} /> Webhooks</span>
            <button onClick={() => onManageWebhooks(it.id)} className="flex items-center gap-1 text-app-2 transition hover:text-app">
              Manage <ArrowRight size={11} />
            </button>
          </div>
          {linkedHooks.length === 0 ? (
            <button
              onClick={() => onManageWebhooks(it.id)}
              className="w-full rounded-lg border border-dashed border-app p-2.5 text-center text-[11px] text-app-2 transition hover:border-app-strong hover:text-app-1"
            >
              No webhooks linked — most providers push events this way. Create one →
            </button>
          ) : (
            <div className="space-y-1">
              {linkedHooks.map((w) => (
                <button
                  key={w.id}
                  onClick={() => onManageWebhooks(it.id)}
                  className="flex w-full items-center gap-2 rounded-lg border border-app bg-surface-2/40 px-2 py-1.5 text-left text-[11px] transition hover:border-app-strong"
                >
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: !w.active ? "#94a3b8" : w.lastStatus === "failed" ? "#f43f5e" : "#22c55e" }}
                  />
                  <span className="min-w-0 flex-1 truncate text-app-1">{w.name}</span>
                  <span className="text-[10px] capitalize text-app-2">{w.direction}</span>
                  <span className="text-[10px] tabular-nums text-app-2">{w.deliveryCount}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* recent events / webhook + sync + errors */}
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2"><Activity size={12} /> Recent events</div>
          {it.events.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-2.5 text-center text-[11px] text-app-2">No events yet</div>
          ) : (
            <div className="space-y-1">
              {it.events.map((e, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: e.tone === "ok" ? "#22c55e" : e.tone === "warn" ? "#f59e0b" : "#f43f5e" }} />
                  <span className="flex-1 text-app-1">{e.label}</span>
                  <span className="text-[10px] text-app-2">{e.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function RequestAccessModal({ it, onClose }: { it: HiveIntegration; onClose: () => void }) {
  const requestAccess = useHive((s) => s.requestAccess);
  const [reason, setReason] = useState(REQUEST_REASONS[0]);
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);

  return (
    <Modal open onClose={onClose} accent="#a855f7" icon={<Lock size={18} />} title="Request access" subtitle={`${it.name} · admin-managed`} width={460}>
      {sent ? (
        <div className="py-6 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-violet-500/15">
            <Check size={28} className="text-violet-500" />
          </motion.div>
          <div className="mt-3 text-sm font-medium text-app">Request submitted</div>
          <div className="mt-1 text-xs text-app-2">Your admin will review access to {it.name}. You'll be notified when it's granted.</div>
          <button onClick={onClose} className="mt-4 rounded-lg border border-app px-4 py-2 text-xs font-medium text-app-1 hover:border-app-strong">Done</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Reason</label>
            <div className="mt-1.5 space-y-1.5">
              {REQUEST_REASONS.map((r) => (
                <button key={r} onClick={() => setReason(r)} className="flex w-full items-center gap-2 rounded-lg border p-2.5 text-left text-xs transition" style={{ borderColor: reason === r ? "#a855f7" : "hsl(var(--border))", background: reason === r ? "#a855f714" : "transparent" }}>
                  <span className="grid h-4 w-4 place-items-center rounded-full border" style={{ borderColor: reason === r ? "#a855f7" : "hsl(var(--border-strong))", background: reason === r ? "#a855f7" : "transparent" }}>
                    {reason === r && <Check size={10} className="text-white" />}
                  </span>
                  <span className="text-app">{r}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add context for your admin…" className="mt-1.5 w-full resize-none rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none focus:border-app-strong placeholder:text-app-2" />
          </div>
          <button onClick={() => { setSent(true); requestAccess(it.id, reason, note); }} className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-white transition hover:brightness-105" style={{ background: "#a855f7" }}>
            <Send size={14} /> Submit request
          </button>
        </div>
      )}
    </Modal>
  );
}

const NEEDS_ATTENTION = (i: HiveIntegration): boolean =>
  ["degraded", "needs-reauth", "pending"].includes(i.status) || Boolean(i.lastError);

export function IntegrationCenterApp() {
  const integrations = useHive((s) => s.integrations);
  const selectedId = useHive((s) => s.selectedIntegrationId);
  const testSweep = useHive((s) => s.testSweep);
  const testAll = useHive((s) => s.testAllIntegrations);
  const hydrateWebhooks = useWebhooks((s) => s.hydrate);
  const [cat, setCat] = useState<(typeof INT_CATEGORIES)[number]>("All");
  const [view, setView] = useState<"grid" | "webhooks" | "topology">("grid");
  const [requestOpen, setRequestOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [q, setQ] = useState("");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [hookFilter, setHookFilter] = useState<string | null>(null);

  // Webhook rows feed the detail panel's "Webhooks" section too, so load early.
  useEffect(() => {
    void hydrateWebhooks();
  }, [hydrateWebhooks]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return integrations.filter(
      (i) =>
        (cat === "All" ? true : cat === "Admin Managed" ? i.admin : i.category === cat) &&
        (!attentionOnly || NEEDS_ATTENTION(i)) &&
        (!needle || i.name.toLowerCase().includes(needle) || i.description.toLowerCase().includes(needle))
    );
  }, [integrations, cat, q, attentionOnly]);
  const selected = integrations.find((i) => i.id === selectedId);

  const counts = {
    connected: integrations.filter((i) => i.status === "connected").length,
    admin: integrations.filter((i) => i.admin).length,
    attention: integrations.filter(NEEDS_ATTENTION).length,
  };
  const allSample = integrations.length > 0 && integrations.every((i) => i.isSample);

  const openWebhooksFor = (integrationId: string | null) => {
    setHookFilter(integrationId);
    setView("webhooks");
  };

  const VIEWS = [
    { id: "grid", icon: LayoutGrid, title: "Connectors" },
    { id: "webhooks", icon: Webhook, title: "Webhooks" },
    { id: "topology", icon: Share2, title: "Topology" },
  ] as const;

  return (
    <div className="flex h-full flex-col">
      <AppHeader
        icon={Plug}
        title="Integration Center"
        subtitle={`${counts.connected} connected · ${counts.admin} admin-managed · ${counts.attention} need attention`}
        accent={accent}
        actions={
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 rounded-lg border border-app bg-surface-2/50 p-0.5">
              {VIEWS.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  className="flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium transition"
                  style={{ background: view === v.id ? accent : "transparent", color: view === v.id ? "#000" : "var(--text-2)" }}
                  title={v.title}
                >
                  <v.icon size={13} /> {v.title}
                </button>
              ))}
            </div>
            <button
              onClick={() => setGuideOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-app px-2.5 py-1.5 text-xs font-medium text-app-1 transition hover:border-app-strong"
              title="How to connect apps and wire webhooks"
            >
              <BookOpen size={13} /> Guide
            </button>
            <button
              disabled={testSweep !== null}
              onClick={() => void testAll()}
              className="flex items-center gap-1.5 rounded-lg border border-app px-2.5 py-1.5 text-xs font-medium text-app-1 transition hover:border-app-strong disabled:opacity-60"
              title="Run a live connectivity test on every connected integration"
            >
              {testSweep ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
              {testSweep ? `Testing ${testSweep.done}/${testSweep.total}` : "Test all"}
            </button>
            <button onClick={() => useHive.getState().setConnectCatalog(true)} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-black transition hover:brightness-105" style={{ background: accent }}>
              <Plus size={13} /> Connect App
            </button>
          </div>
        }
      />

      {view === "grid" && (
        <>
          {/* category tabs */}
          <div className="flex items-center gap-1 overflow-x-auto border-b border-app px-4 py-2 no-scrollbar">
            {INT_CATEGORIES.map((c) => {
              const n = c === "All" ? integrations.length : c === "Admin Managed" ? counts.admin : integrations.filter((i) => i.category === c).length;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition"
                  style={{ background: cat === c ? `${accent}1f` : "transparent", color: cat === c ? accent : "var(--text-2)" }}
                >
                  {c === "Admin Managed" && <Lock size={11} />}
                  {c}
                  <span className="rounded-full bg-surface-2 px-1.5 text-[10px] tabular-nums text-app-2">{n}</span>
                </button>
              );
            })}
          </div>

          {/* search + attention filter */}
          <div className="flex items-center gap-2 border-b border-app px-4 py-2">
            <div className="flex max-w-xs flex-1 items-center gap-1.5 rounded-lg border border-app bg-surface px-2.5 py-1.5">
              <Search size={12} className="shrink-0 text-app-2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search integrations…"
                className="w-full bg-transparent text-xs text-app outline-none placeholder:text-app-2"
              />
              {q && (
                <button onClick={() => setQ("")} className="text-app-2 hover:text-app">
                  <X size={11} />
                </button>
              )}
            </div>
            <button
              onClick={() => setAttentionOnly((v) => !v)}
              className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition"
              style={{
                borderColor: attentionOnly ? "#f43f5e66" : "hsl(var(--border))",
                background: attentionOnly ? "#f43f5e14" : "transparent",
                color: attentionOnly ? "#f43f5e" : "var(--text-2)",
              }}
            >
              <AlertTriangle size={11} /> Needs attention
              <span className="rounded-full bg-surface-2 px-1.5 text-[10px] tabular-nums">{counts.attention}</span>
            </button>
          </div>
        </>
      )}

      {view === "topology" ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto p-6">
          <div className="mb-2 text-center">
            <div className="text-sm font-semibold text-app">Connection topology</div>
            <div className="text-xs text-app-2">How Hive routes data between your systems and the agent fleet</div>
          </div>
          <Topology />
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3 text-[11px] text-app-2">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Hive subsystems</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: accent }} /> External integrations</span>
          </div>
        </div>
      ) : view === "webhooks" ? (
        <WebhooksPanel
          integrationFilter={hookFilter}
          onClearIntegrationFilter={() => setHookFilter(null)}
          onOpenGuide={() => setGuideOpen(true)}
        />
      ) : (
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-y-auto no-scrollbar p-4">
            {allSample && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-app bg-surface-2/40 px-3.5 py-2.5">
                <SampleBadge />
                <span className="text-xs leading-relaxed text-app-2">
                  Everything below is sample data — inert by design, no live calls. Connect a real app to see live
                  health, errors and webhooks.
                </span>
                <span className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={() => useHive.getState().setConnectCatalog(true)}
                    className="rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
                    style={{ background: accent }}
                  >
                    Connect a real app
                  </button>
                  <button
                    onClick={() => setGuideOpen(true)}
                    className="rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
                  >
                    How it works
                  </button>
                </span>
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="py-14 text-center text-sm text-app-2">
                {attentionOnly ? "Nothing needs attention — every integration is healthy." : `No integrations match${q ? ` "${q}"` : ""}.`}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <AnimatePresence>
                  {filtered.map((it, i) => (
                    <IntegrationCard key={it.id} it={it} index={i} />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          <div className="hidden w-[400px] shrink-0 border-l border-app lg:block">
            <AnimatePresence mode="wait">
              {selected ? (
                <IntegrationDetail it={selected} onRequest={() => setRequestOpen(true)} onManageWebhooks={openWebhooksFor} />
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid h-full place-items-center p-8 text-center">
                  <div>
                    <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-app-2"><Plug size={22} /></div>
                    <p className="mt-3 text-sm text-app-2">Select an integration to view its scopes, health, linked workspaces and security posture.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {selected && requestOpen && <RequestAccessModal it={selected} onClose={() => setRequestOpen(false)} />}
      <ConnectAppModal />
      <IntegrationGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
